// Bot Management Configuration — redesigned per the design bundle
// `redesign/bot-management.html`. Same data hooks as the previous
// implementation (`useBMConfig`, `patchBotConfig`, `stagePatchS3`);
// new visual structure: smart-suggestion ribbon, action picker cards,
// segmented detection mode + composite slider, signals toggles,
// sensitivity preset cards + advanced floors, sticky right-rail with
// live impact placeholders, sticky save bar when dirty.
//
// Brand palette: indigo `colors.primary[300]` matches the design's
// canonical `--primary-indigo` (#6371F7). All typography flows from
// the existing v2 Rubik tokens.
import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import {
  Stack, Typography, Box, InputLabel, Select, MenuItem, Slider, IconButton,
  Tooltip, Chip, FormHelperText,
} from "@mui/material";

import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import PowerSettingsNewOutlinedIcon from "@mui/icons-material/PowerSettingsNewOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import RadioOutlinedIcon from "@mui/icons-material/RadioOutlined";
import SettingsSuggestOutlinedIcon from "@mui/icons-material/SettingsSuggestOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import GppMaybeOutlinedIcon from "@mui/icons-material/GppMaybeOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import ExpandLessOutlinedIcon from "@mui/icons-material/ExpandLessOutlined";
import { ReactComponent as ArrowdownIcon } from "../../../../../vendor/_new/icon/sites/arrow_down.svg";
import { ReactComponent as EditIcon } from "../../../../../vendor/_new/icon/sites/edit.svg";
import colors from "../../../../../utils/colors";
import V2PageHeader from "../../../../common/V2PageHeader";
import SiteScopeGate from "../../../../common/SiteScopeGate";
import { GreenSelectForm, GreenSwitch, SafeTextField } from "../../../../common/Greens";
import { ConfigAction, FirewallAction, BotType, NetflowKeyMode, ChallengeKind } from "../../../../../utils/constants";
import { useNotification } from "../../../../../contexts/NotificationContext";
import useSite from "../../../../../hooks/user/useSite";
import useGlobalSite from "../../../../../hooks/user/useGlobalSite";
import useAuth from "../../../../../hooks/useAuth";
import usePermissions from "../../../../../hooks/usePermissions";
import useBMConfig from "../../../../../hooks/user/useBMConfig";
import { RefreshButton } from "../../../../common/Button";
import StickySaveBar from "../../../../common/StickySaveBar";
import FormHelpTooltip from "../../../../common/FormHelpTooltip";
import { collectPendingDiff, enabledLabel } from "../../../../common/stickySaveBarUtils";
import { validateBmConfigForm } from "../../../../../utils/validation";
import userAxios from "../../../../../utils/axios/v1/userAxios";

// Design tokens — kept inline so this page is self-contained but every
// value maps to an existing `colors.*` entry where possible. The few
// hex literals (warning/danger pastels) match the design exactly.
const INDIGO = colors.primary[300];
const INDIGO_BG = "rgba(99, 113, 247, 0.08)";
const BORDER = colors.neutral[200];
const BORDER_STRONG = colors.neutral[500];
const FG1 = colors.neutral[1000];
const FG2 = colors.neutral[700];
const FG3 = colors.neutral[500];
const SUCCESS = "#1FC16B";
const WARNING = "#F2B429";
const DANGER = "#FB3748";

const helperTextSx = { fontSize: 12, color: FG3, mx: 0, mt: 0.5 };

function firewallActionLabel(v) {
  const map = {
    [FirewallAction.ALLOW]: "Allow",
    [FirewallAction.BLOCK]: "Block",
    [FirewallAction.CHALLENGE]: "Challenge",
    [FirewallAction.LOG]: "Log only",
    [FirewallAction.DROP]: "Drop",
  };
  return map[v] ?? String(v ?? "—");
}

function bmModeLabel(v) {
  const n = Number(v);
  if (n === 0) return "Off";
  if (n === 2) return "Log only";
  if (n === 1) return "Enforce";
  return String(v ?? "—");
}

function aiBotModeLabel(v) {
  if (v === "off") return "Off";
  if (v === "log_only") return "Log only";
  if (v === "block") return "Block";
  return String(v ?? "—");
}

const BM_PENDING_LABELS = {
  enabled: "Bot management",
  bad_bot_action: "Bad bot action",
  good_bot_action: "Good bot action",
  bm_challenge_kind: "Challenge kind",
  bm_mode: "Detection mode",
  unverified_threshold: "Unverified threshold",
  unverified_action: "Unverified action",
  composite_scoring_enabled: "Composite scoring",
  composite_block_threshold: "Composite block threshold",
  netflow_enabled: "Netflow signals",
  netflow_key_mode: "Netflow key mode",
  polymorphic_js_enabled: "Polymorphic JS",
  inline_engine_scoring: "Inline engine scoring",
  bm_ai_bot_enforcement: "AI bot enforcement",
  bm_rate_limit_max_human_visits: "Rate limit (human visits)",
  bm_hammer_floor: "Same-URI hammer floor",
  bm_uri_diversity_ratio: "URI diversity ratio",
  bm_url_enum_floor: "URL enumeration floor",
  bm_spike: "Spike (reqs / 1s)",
  bm_dense: "Dense (reqs / 5s)",
  bm_burst_min_samples: "Burst min samples",
  bm_burst_cv_ceiling: "Burst CV ceiling",
  bm_burst_mean_max: "Burst mean (sec)",
};

const BM_PENDING_FORMATTERS = {
  enabled: enabledLabel,
  composite_scoring_enabled: enabledLabel,
  netflow_enabled: enabledLabel,
  polymorphic_js_enabled: enabledLabel,
  inline_engine_scoring: enabledLabel,
  bad_bot_action: firewallActionLabel,
  good_bot_action: firewallActionLabel,
  unverified_action: firewallActionLabel,
  bm_mode: bmModeLabel,
  bm_ai_bot_enforcement: aiBotModeLabel,
};

// ── Section primitives ────────────────────────────────────────────
function SectionCard({ icon, title, desc, helpTooltip, right, children, bodyPad = "18px 20px" }) {
  return (
    <Box sx={{
      background: "#fff", border: `1px solid ${BORDER}`, borderRadius: "14px", overflow: "hidden",
    }}>
      <Box sx={{
        padding: "18px 20px 14px", display: "flex", alignItems: "flex-start",
        justifyContent: "space-between", gap: 1.75, borderBottom: `1px solid ${BORDER}`,
      }}>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", minWidth: 0 }}>
          {icon && (
            <Box sx={{
              width: 36, height: 36, borderRadius: "9px",
              background: INDIGO_BG, color: INDIGO,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>{icon}</Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" alignItems="center" sx={{ flexWrap: "nowrap" }}>
              <Typography sx={ { fontWeight: 600, fontSize: 16, color: FG1, mb: 0.25 }}>
                {title}
              </Typography>
              <FormHelpTooltip tooltip={helpTooltip} />
            </Stack>
            {desc && (
              <Typography sx={ { fontSize: 12.5, color: FG2, lineHeight: 1.5, maxWidth: 540 }}>
                {desc}
              </Typography>
            )}
          </Box>
        </Box>
        {right}
      </Box>
      {children !== undefined && <Box sx={{ padding: bodyPad }}>{children}</Box>}
    </Box>
  );
}

// One picker card. Replaces the legacy "select dropdown" — operator
// taps the card matching the action they want; ring + check icon
// signal selection.
function ActionCard({ id, active, onPick, icon, iconBg, iconColor, title, desc, disabled }) {
  const on = active === id;
  return (
    <Box
      onClick={() => !disabled && onPick(id)}
      sx={{
        display: "flex", flexDirection: "column", gap: 1,
        padding: "14px", borderRadius: "10px",
        border: `1px solid ${on ? INDIGO : BORDER}`,
        background: on ? "rgba(99,113,247,0.04)" : "#fff",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        transition: "all 150ms",
        "&:hover": disabled ? undefined : { borderColor: on ? INDIGO : BORDER_STRONG },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{
          width: 26, height: 26, borderRadius: "7px",
          background: iconBg, color: iconColor,
          display: "flex", alignItems: "center", justifyContent: "center",
          "& svg": { width: 14, height: 14 },
        }}>
          {icon}
        </Box>
        <Typography sx={ { fontWeight: 600, fontSize: 13, color: FG1, flex: 1 }}>
          {title}
        </Typography>
        <Box sx={{
          width: 16, height: 16, borderRadius: "50%",
          border: `1.5px solid ${on ? INDIGO : BORDER}`,
          background: on ? INDIGO : "transparent",
          color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "center",
          "& svg": { width: 10, height: 10 },
        }}>
          {on && <CheckOutlinedIcon />}
        </Box>
      </Box>
      <Typography sx={ { fontSize: 11.5, color: FG2, lineHeight: 1.5 }}>
        {desc}
      </Typography>
    </Box>
  );
}

// Sensitivity preset card. Three of these on a row — Low / Medium / High.
function SensitivityCard({ id, active, onPick, bars, title, desc }) {
  const on = active === id;
  return (
    <Box
      onClick={() => onPick(id)}
      sx={{
        padding: "14px", border: `1.5px solid ${on ? INDIGO : BORDER}`,
        borderRadius: "12px", cursor: "pointer", background: "#fff",
        transition: "all 150ms", textAlign: "left",
        ...(on && {
          background: "linear-gradient(160deg, rgba(99,113,247,0.06) 0%, transparent 60%)",
        }),
        "&:hover": { borderColor: on ? INDIGO : BORDER_STRONG },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography sx={ { fontWeight: 600, fontSize: 13.5, color: FG1 }}>
          {title}
        </Typography>
        <Box sx={{ display: "flex", gap: "3px", alignItems: "flex-end", height: 18 }}>
          {[6, 10, 14].map((h, i) => (
            <Box key={i} sx={{ width: 4, height: h, borderRadius: "1px", background: bars > i ? INDIGO : colors.neutral[300] }} />
          ))}
        </Box>
      </Box>
      <Typography sx={ { fontSize: 11.5, color: FG2, lineHeight: 1.5 }}>
        {desc}
      </Typography>
    </Box>
  );
}

// Segmented control — soft indigo tint for the active segment on neutral-50 track.
function Segmented({ value, onChange, options, disabled }) {
  return (
    <Box sx={{
      display: "flex",
      background: colors.neutral[50],
      border: `1px solid ${BORDER}`,
      borderRadius: "10px", padding: "4px", gap: "2px", width: "100%",
      opacity: disabled ? 0.6 : 1,
      pointerEvents: disabled ? "none" : "auto",
    }}>
      {options.map((opt) => {
        const on = value === opt.value;
        const idleColor = opt.tone === "log" ? "#7a5b10"
                        : opt.tone === "enforce" ? "#A11228"
                        : FG2;
        const activeColor = opt.tone === "log" ? "#7a5b10"
                          : opt.tone === "enforce" ? "#A11228"
                          : INDIGO;
        return (
          <Box
            key={opt.value}
            onClick={() => !disabled && onChange(opt.value)}
            sx={{
              flex: 1, padding: "8px 10px", fontSize: 13, fontWeight: on ? 600 : 500,
              borderRadius: "7px",
              color: on ? activeColor : idleColor,
              background: on ? INDIGO_BG : "transparent",
              border: on ? "1px solid rgba(99,113,247,0.22)" : "1px solid transparent",
              boxShadow: on ? "0 1px 2px rgba(99,113,247,0.08)" : "none",
              cursor: disabled ? "not-allowed" : "pointer",
              transition: "all 150ms",
              display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
whiteSpace: "nowrap",
              "&:hover": on || disabled ? undefined : { color: FG1, background: "rgba(99,113,247,0.06)" },
              "& svg": { width: 14, height: 14 },
            }}
          >
            {opt.icon}{opt.label}
          </Box>
        );
      })}
    </Box>
  );
}

function bmModeToSegment(mode) {
  const m = Number(mode);
  if (m === 0) return "off";
  if (m === 2) return "log";
  return "enforce";
}

// Switch row — used in Signals + Composite scoring.
function SwitchRow({ title, desc, checked, onChange, disabled, tinted, chip }) {
  return (
    <Box sx={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      gap: 1.75, padding: "12px 14px",
      border: tinted ? "none" : `1px solid ${BORDER}`,
      background: tinted ? colors.neutral[100] : "#fff",
      borderRadius: "10px",
    }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={ { fontSize: 13.5, fontWeight: 500, color: FG1 }}>
            {title}
          </Typography>
          {chip}
        </Box>
        <Typography sx={ { fontSize: 11.5, color: FG2 }}>{desc}</Typography>
      </Box>
      <GreenSwitch checked={checked} onChange={onChange} disabled={disabled} />
    </Box>
  );
}

// Number stepper used for the 9 advanced floors.
function NumStepper({ value, onChange, step = 1, min = 0, max = Infinity, suffix, disabled }) {
  const dec = () => onChange(Math.max(min, +(value - step).toFixed(3)));
  const inc = () => onChange(Math.min(max, +(value + step).toFixed(3)));
  return (
    <Box sx={{
      display: "flex", alignItems: "center", border: `1px solid ${BORDER}`,
      borderRadius: "8px", overflow: "hidden", height: 38, background: "#fff",
      opacity: disabled ? 0.5 : 1,
      "&:focus-within": { borderColor: INDIGO, boxShadow: `0 0 0 3px rgba(99,113,247,0.18)` },
    }}>
      <Box onClick={() => !disabled && dec()} sx={{
        width: 32, height: "100%", display: "flex", alignItems: "center", justifyContent: "center",
        color: FG2, fontSize: 14, borderRight: `1px solid rgba(0,0,0,0.06)`,
        cursor: disabled ? "not-allowed" : "pointer", "&:hover": disabled ? undefined : { background: colors.neutral[100], color: FG1 },
      }}>−</Box>
      <input
        type="text"
        value={value}
        disabled={disabled}
        onChange={(e) => {
          const n = parseFloat(e.target.value);
          if (!isNaN(n)) onChange(Math.max(min, Math.min(max, n)));
        }}
        style={{
          flex: 1, border: 0, outline: 0, textAlign: "center",
          font: "inherit", fontWeight: 500,
          background: "transparent", color: FG1, minWidth: 0, width: "100%",
        }}
      />
      {suffix && (
        <Typography sx={{
          fontFamily: "monospace", fontSize: 11, color: FG3, mr: 1, whiteSpace: "nowrap",
        }}>{suffix}</Typography>
      )}
      <Box onClick={() => !disabled && inc()} sx={{
        width: 32, height: "100%", display: "flex", alignItems: "center", justifyContent: "center",
        color: FG2, fontSize: 14, borderLeft: `1px solid rgba(0,0,0,0.06)`,
        cursor: disabled ? "not-allowed" : "pointer", "&:hover": disabled ? undefined : { background: colors.neutral[100], color: FG1 },
      }}>+</Box>
    </Box>
  );
}

// Field wrapper — label + control + optional hint / validation error.
function Field({ label, hint, required, error, children }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, minWidth: 0 }}>
      <Typography sx={ { fontSize: 12, fontWeight: 500, color: FG2 }}>
        {label}
        {required && (
          <Typography component="span" sx={{ color: DANGER, ml: 0.25 }}>*</Typography>
        )}
      </Typography>
      {children}
      {error ? (
        <FormHelperText error sx={helperTextSx}>{error}</FormHelperText>
      ) : hint ? (
        <Typography sx={ { fontSize: 11.5, color: FG3, lineHeight: 1.45 }}>{hint}</Typography>
      ) : null}
    </Box>
  );
}

// ── Live rail (right column) ──────────────────────────────────────
// Three panels backed by CH-driven endpoints on the CP:
//   * /config/bot/:site_uid/bm-events/mix      (60-min default)
//   * /config/bot/:site_uid/bm-events/applied  (24h actuals)
//   * /config/bot/:site_uid/bm-events/categories (24h top-N)
// Polls every 30s while mounted. All numbers are real — empty
// states render explicitly when CH has no rows in the window
// (instead of fake placeholder digits).
function LiveImpactRail({ siteUuid }) {
  // The URL :site_uid param must be the UUID — auth_config rejects
  // the text-form siteId with a 404. Caller passes selectedSite.id.
  const [mix, setMix] = useState(null);
  const [applied, setApplied] = useState(null);
  const [cats, setCats] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!siteUuid) return;
    try {
      const [m, a, c] = await Promise.all([
        userAxios.get(`config/bot/${siteUuid}/bm-events/mix`).catch(() => ({ data: null })),
        userAxios.get(`config/bot/${siteUuid}/bm-events/applied`).catch(() => ({ data: null })),
        userAxios.get(`config/bot/${siteUuid}/bm-events/categories`).catch(() => ({ data: null })),
      ]);
      setMix(m?.data || null);
      setApplied(a?.data || null);
      setCats(Array.isArray(c?.data?.items) ? c.data.items : []);
    } catch {
      // catch covers Promise.all rejection — individual setters above
      // already fallback to null, so we silently swallow.
    }
  }, [siteUuid]);

  useEffect(() => {
    fetchAll();
    const h = setInterval(fetchAll, 30_000);
    return () => clearInterval(h);
  }, [fetchAll]);

  const mixTotal = mix?.total || 0;
  const mixSeg = (n) => (mixTotal > 0 ? Math.max(1, Math.round((n / mixTotal) * 100)) : 0);

  return (
    <Stack spacing={2}>
      {/* Bot traffic mix */}
      <Box sx={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: "14px", padding: "16px 18px" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
          <Box>
            <Typography sx={{ fontFamily: "Plus Jakarta Sans, rubik", fontSize: 11, color: FG2, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>
              Live · last 60 min
            </Typography>
            <Typography sx={ { fontSize: 13.5, fontWeight: 600, color: FG1, mt: 0.5 }}>
              Bot traffic mix
            </Typography>
          </Box>
          <Chip label="streaming" size="small" sx={{
fontSize: 11, height: 22,
            background: "#F6FCED", color: "#0f7a42", borderRadius: "999px",
          }} />
        </Stack>
        {mix === null ? (
          <Typography sx={ { fontSize: 11.5, color: FG3, py: 2, textAlign: "center" }}>Loading…</Typography>
        ) : mixTotal === 0 ? (
          <Typography sx={ { fontSize: 11.5, color: FG3, py: 2, textAlign: "center" }}>
            No requests in the last 60 minutes.
          </Typography>
        ) : (
          <>
            <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 1 }}>
              <Typography sx={{ fontFamily: "monospace", fontSize: 26, fontWeight: 700, color: FG1, letterSpacing: "-0.01em" }}>
                {(((mix.bad_bot + mix.unverified) / mixTotal) * 100).toFixed(1)}%
              </Typography>
              <Typography sx={ { fontSize: 11.5, color: FG2 }}>
                of {mixTotal.toLocaleString()} reqs
              </Typography>
            </Stack>
            <Box sx={{ display: "flex", height: 8, borderRadius: "99px", overflow: "hidden", background: colors.neutral[150], mt: 1 }}>
              <Box sx={{ width: `${mixSeg(mix.human)}%`, background: SUCCESS }} />
              <Box sx={{ width: `${mixSeg(mix.good_bot)}%`, background: INDIGO }} />
              <Box sx={{ width: `${mixSeg(mix.unverified)}%`, background: WARNING }} />
              <Box sx={{ width: `${mixSeg(mix.bad_bot)}%`, background: DANGER }} />
            </Box>
            <Stack spacing={0.75} sx={{ mt: 1.5 }}>
              {[
                ["Human", mix.human, SUCCESS],
                ["Good bot · verified", mix.good_bot, INDIGO],
                ["Unverified", mix.unverified, WARNING],
                ["Bad bot · blocked", mix.bad_bot, DANGER],
              ].map(([label, n, c]) => (
                <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: 11.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "2px", background: c }} />
                  <Typography sx={{ flex: 1, fontSize: 11.5, color: FG2 }}>{label}</Typography>
                  <Typography sx={{ fontFamily: "monospace", fontWeight: 600, color: FG1, fontSize: 11.5 }}>
                    {n.toLocaleString()}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </>
        )}
      </Box>

      {/* Applied-state rollup (replaces "If applied now" simulation —
          we show actuals over the same window so operators see what
          their CURRENT config produced, not a synthetic projection). */}
      <Box sx={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: "14px", padding: "16px 18px" }}>
        <Typography sx={ { fontSize: 13.5, fontWeight: 600, color: FG1, mb: 1.25 }}>
          Current outcomes · last 24h
        </Typography>
        {applied === null ? (
          <Typography sx={ { fontSize: 11.5, color: FG3, py: 2, textAlign: "center" }}>Loading…</Typography>
        ) : applied.total === 0 ? (
          <Typography sx={ { fontSize: 11.5, color: FG3, py: 2, textAlign: "center" }}>
            No BM events in the last 24 hours.
          </Typography>
        ) : (
          <Stack spacing={0.75}>
            {[
              ["Blocked", applied.blocked, DANGER],
              ["Challenged", applied.challenged, WARNING],
              ["Allowed (verified)", applied.allowed_verified, SUCCESS],
              ["Allowed (total)", applied.allowed_total, FG1],
            ].map(([k, v, color]) => (
              <Box key={k} sx={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "8px 10px", borderRadius: "8px", background: colors.neutral[100],
fontSize: 12.5, color: FG2,
              }}>
                <span>{k}</span>
                <Typography sx={{ fontFamily: "monospace", fontWeight: 600, color, fontSize: 12.5 }}>
                  {v.toLocaleString()}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      {/* Top categories · 24h */}
      <Box sx={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: "14px", padding: "16px 18px" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.25 }}>
          <Typography sx={ { fontSize: 13.5, fontWeight: 600, color: FG1 }}>
            Top bot categories · 24h
          </Typography>
          <Chip label="24h" size="small" sx={ { fontSize: 11, height: 22, background: colors.neutral[100], color: FG2, borderRadius: "999px" }} />
        </Stack>
        {cats === null ? (
          <Typography sx={ { fontSize: 11.5, color: FG3, py: 2, textAlign: "center" }}>Loading…</Typography>
        ) : cats.length === 0 ? (
          <Typography sx={ { fontSize: 11.5, color: FG3, py: 2, textAlign: "center" }}>
            No categorised bot traffic in the last 24h.
          </Typography>
        ) : (
          <Stack spacing={0.5}>
            {cats.map((r) => {
              const color = r.severity === "danger" ? DANGER
                : r.severity === "ok" ? SUCCESS
                : r.severity === "info" ? INDIGO
                : WARNING;
              return (
                <Box key={r.category} sx={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "10px 12px", background: "#fff", border: `1px solid ${BORDER}`,
                  borderRadius: "8px", fontSize: 13,
                }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
                    <Typography sx={ { fontSize: 12.5, fontWeight: 500, color: FG1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {prettifyCategory(r.category)}
                    </Typography>
                  </Stack>
                  <Typography sx={{ fontFamily: "monospace", fontWeight: 600, color: FG1, fontSize: 12 }}>
                    {r.count.toLocaleString()}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    </Stack>
  );
}

// `suspicious_uri_probe` → "Suspicious URI probe".
function prettifyCategory(c) {
  if (!c) return "—";
  return c.replace(/_/g, " ").replace(/\b\w/g, (s) => s.toUpperCase());
}

// ── Main page ─────────────────────────────────────────────────────
const NewServicesBotConfig = () => {
  const { isAuthenticated } = useAuth();
  const { siteList, getSitesForItems, settingApply } = useSite();
  const { getPermissionsForSite, PERMISSIONS } = usePermissions();
  const { botConfig, getBmConfig, enableBm, setBotAction, setErr, errMsg } = useBMConfig();
  const { showSuccess, showError } = useNotification();
  const { effectiveSiteUid } = useGlobalSite();
  const siteID = effectiveSiteUid;
  const [dirty, setDirty] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const selectedSite = useMemo(() => (siteID && siteList ? siteList.find((s) => s.id === siteID) ?? null : null), [siteList, siteID]);
  const siteSiteId = useMemo(() => (selectedSite?.site_id ?? null), [selectedSite]);

  const { canReadBotForSite, canConfigureBotForSite, canApplyBotDeploymentForSite } = useMemo(() => {
    if (!selectedSite) return { canReadBotForSite: false, canConfigureBotForSite: false, canApplyBotDeploymentForSite: false };
    const sp = getPermissionsForSite(selectedSite);
    const read = sp.hasPermission(PERMISSIONS.BOT_READ);
    const botCfg = sp.hasPermission(PERMISSIONS.BOT_CONFIGURE);
    const ddosCfg = sp.hasPermission(PERMISSIONS.DDOS_CONFIGURE);
    return { canReadBotForSite: read, canConfigureBotForSite: botCfg, canApplyBotDeploymentForSite: botCfg && ddosCfg };
  }, [selectedSite, getPermissionsForSite, PERMISSIONS]);

  // Local form state (mirrors botConfig; updated optimistically).
  const [bmEnabled, setBmEnabled] = useState(false);
  const [goodBotAction, setGoodBotAction] = useState(FirewallAction.ALLOW);
  const [badBotAction, setBadBotAction] = useState(FirewallAction.BLOCK);
  const [bmMode, setBmMode] = useState(1);
  const [unverifiedThreshold, setUnverifiedThreshold] = useState(5);
  const [unverifiedAction, setUnverifiedAction] = useState(FirewallAction.CHALLENGE);
  // bmChallengeKind: nullable challenge variant; only meaningful when any
  // of the bot actions (bad/good/unverified) is Challenge. NULL means
  // "let backend default (managed)"; PATCH key is `bm_challenge_kind`.
  const [bmChallengeKind, setBmChallengeKind] = useState(null);
  const [compositeScoringEnabled, setCompositeScoringEnabled] = useState(false);
  const [compositeBlockThreshold, setCompositeBlockThreshold] = useState(80);
  const [netflowEnabled, setNetflowEnabled] = useState(false);
  const [netflowKeyMode, setNetflowKeyMode] = useState(NetflowKeyMode.JA4);
  const [polymorphicJsEnabled, setPolymorphicJsEnabled] = useState(false);
  // inline_engine_scoring: when true (default), the data plane calls the
  // BM engine synchronously on cache-miss requests for a per-request
  // verdict. Off = no engine call; cache falls back to the
  // unverified-threshold counter (challenge everything past N from a
  // source-tuple). Toggle is shipped per `M1 (2026-06-25)`.
  const [inlineEngineScoring, setInlineEngineScoring] = useState(true);
  // Sensitivity preset state — preset name only (the 9 floors live
  // server-side; the preset choice resolves to a known floor set).
  const [sensitivity, setSensitivity] = useState("medium");
  // AI bot enforcement — three-state per-site toggle. Default "off"
  // preserves backwards-compat (legacy AI_CRAWLER UAs in bad_ua still
  // block by default; this toggle only governs the modern UA list
  // seeded into bm:ai_agents:ua).
  const [aiBotMode, setAiBotMode] = useState("off");
  // The 9 advanced floors (UI-only; backend acceptance via
  // PATCH /config/bot/:site_uid/config with the bm_* fields per
  // memory `bm_thresholds_ui_configurable.md`).
  const [floors, setFloors] = useState({
    rate_limit: 300, hammer_floor: 50, uri_diversity: 0.1, url_enum: 20,
    spike: 30, dense: 60, burst_min: 10, burst_cv: 0.3, burst_mean: 5,
  });

  useEffect(() => {
    if (errMsg) { showError(errMsg); setErr(null); }
  }, [errMsg, showError, setErr]);

  // Pending changes — written into local form state immediately so
  // the UI is responsive, but NOT pushed to the server until the
  // operator clicks Apply. Single bucket of (column → value) writes
  // straight to PATCH /config/bot/:siteUid/config.
  const [pending, setPending] = useState({});
  const stagePatch = useCallback((body) => {
    if (!canConfigureBotForSite) return;
    setPending((prev) => ({ ...prev, ...body }));
    setDirty(true);
  }, [canConfigureBotForSite]);

  const effectiveEnabled = pending.enabled ?? bmEnabled;
  const effectiveBadBotAction = pending.bad_bot_action ?? badBotAction;
  const effectiveChallengeKind = "bm_challenge_kind" in pending ? pending.bm_challenge_kind : bmChallengeKind;

  const formValidation = useMemo(
    () => validateBmConfigForm({
      enabled: effectiveEnabled,
      bad_bot_action: effectiveBadBotAction,
      bm_challenge_kind: effectiveChallengeKind,
    }),
    [effectiveEnabled, effectiveBadBotAction, effectiveChallengeKind],
  );

  const touchField = useCallback((field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  const showFieldErr = useCallback(
    (field) => (submitAttempted || touched[field]) && formValidation.errors[field],
    [submitAttempted, touched, formValidation.errors],
  );

  const validateBeforeSave = useCallback(() => {
    setSubmitAttempted(true);
    const validation = validateBmConfigForm({
      enabled: effectiveEnabled,
      bad_bot_action: effectiveBadBotAction,
      bm_challenge_kind: effectiveChallengeKind,
    });
    if (!validation.valid) {
      return false;
    }
    return true;
  }, [effectiveEnabled, effectiveBadBotAction, effectiveChallengeKind]);

  const flushPending = useCallback(async () => {
    if (!siteID || !canConfigureBotForSite) return true;
    if (Object.keys(pending).length === 0) return true;
    if (!validateBeforeSave()) return false;
    try {
      await userAxios.patch(`config/bot/${siteID}/config`, pending);
      setPending({});
      setDirty(false);
      setSubmitAttempted(false);
      setTouched({});
      return true;
    } catch (err) {
      showError(err?.response?.data?.message || err?.message || "Failed to save changes");
      return false;
    }
  }, [siteID, canConfigureBotForSite, pending, showError, validateBeforeSave]);

  // (S3 export now flows through stagePatchS3 + flushPending — no
  // separate immediate-save helper. Kept the empty placeholder so
  // memory-debugging never blames a name-resolution error.)

  // Hydrate from botConfig on load.
  useEffect(() => {
    if (botConfig) {
      setBmEnabled(botConfig.enabled);
      setGoodBotAction(botConfig.good_bot_action);
      setBadBotAction(botConfig.bad_bot_action);
      setBmMode(Number(botConfig.bm_mode ?? 1));
      setUnverifiedThreshold(botConfig.unverified_threshold ?? 5);
      setUnverifiedAction(botConfig.unverified_action ?? FirewallAction.CHALLENGE);
      setBmChallengeKind(botConfig.bm_challenge_kind ?? null);
      setCompositeScoringEnabled(botConfig.composite_scoring_enabled ?? false);
      setCompositeBlockThreshold(botConfig.composite_block_threshold ?? 80);
      setNetflowEnabled(botConfig.netflow_enabled ?? false);
      setNetflowKeyMode(botConfig.netflow_key_mode ?? NetflowKeyMode.JA4);
      setPolymorphicJsEnabled(botConfig.polymorphic_js_enabled ?? false);
      setInlineEngineScoring(botConfig.inline_engine_scoring ?? true);
      setAiBotMode(botConfig.bm_ai_bot_enforcement ?? "off");
      // 9 advanced floors — hydrate from bm_* fields when present.
      setFloors({
        rate_limit: botConfig.bm_rate_limit_max_human_visits ?? 300,
        hammer_floor: botConfig.bm_hammer_floor ?? 50,
        uri_diversity: botConfig.bm_uri_diversity_ratio ?? 0.1,
        url_enum: botConfig.bm_url_enum_floor ?? 20,
        spike: botConfig.bm_spike ?? 30,
        dense: botConfig.bm_dense ?? 60,
        burst_min: botConfig.bm_burst_min_samples ?? 10,
        burst_cv: botConfig.bm_burst_cv_ceiling ?? 0.3,
        burst_mean: botConfig.bm_burst_mean_max ?? 5,
      });
      setDirty(false);
      setSubmitAttempted(false);
      setTouched({});
    }
  }, [botConfig]);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  // Apply: stage 1 flush pending field changes → stage 2 push the
  // deployment via settingApply. StickySaveBar triggers this so a
  // single click persists the form AND deploys to Layerd. If a flush
  // fails (validation / network), abort the deployment so PG and
  // Layerd don't diverge.
  const apply = async () => {
    if (!canApplyBotDeploymentForSite) return;
    setIsApplying(true);
    try {
      const ok = await flushPending();
      if (!ok) return;
      // Refetch so the form snaps to the post-save server state and
      // the staged-state buckets are confirmed empty.
      if (siteID) await getBmConfig(siteID);
      if (selectedSite) {
        const result = await settingApply(selectedSite.site_id, ConfigAction.BOT_MANAGEMENT);
        if (result.status === "success") showSuccess(result.msg || "Applied to data plane");
        else showError(result.msg || "Apply failed");
      }
    } finally {
      setIsApplying(false);
    }
  };
  const refresh = async () => {
    if (!siteID || !canReadBotForSite) return;
    setIsRefreshing(true);
    await getBmConfig(siteID);
    setIsRefreshing(false);
  };

  const [isInitialized, setIsInitialized] = useState(true);
  useEffect(() => {
    if (isAuthenticated) getSitesForItems().then(() => setIsInitialized(false));
  }, [isAuthenticated, getSitesForItems]);

  useEffect(() => {
    if (!siteID || !canReadBotForSite) return;
    getBmConfig(siteID);
  }, [siteID, canReadBotForSite, getBmConfig]);

  // Apply a sensitivity preset → updates the 9 floors + saves.
  const presets = {
    low:    { rate_limit: 600, hammer_floor: 100, uri_diversity: 0.2,  url_enum: 40, spike: 50, dense: 100, burst_min: 20, burst_cv: 0.5,  burst_mean: 8 },
    medium: { rate_limit: 300, hammer_floor: 50,  uri_diversity: 0.1,  url_enum: 20, spike: 30, dense: 60,  burst_min: 10, burst_cv: 0.3,  burst_mean: 5 },
    high:   { rate_limit: 150, hammer_floor: 25,  uri_diversity: 0.05, url_enum: 10, spike: 15, dense: 30,  burst_min: 5,  burst_cv: 0.15, burst_mean: 3 },
  };
  const onPreset = (key) => {
    setSensitivity(key);
    setFloors(presets[key]);
    stagePatch({
      bm_rate_limit_max_human_visits: presets[key].rate_limit,
      bm_hammer_floor: presets[key].hammer_floor,
      bm_uri_diversity_ratio: presets[key].uri_diversity,
      bm_url_enum_floor: presets[key].url_enum,
      bm_spike: presets[key].spike,
      bm_dense: presets[key].dense,
      bm_burst_min_samples: presets[key].burst_min,
      bm_burst_cv_ceiling: presets[key].burst_cv,
      bm_burst_mean_max: presets[key].burst_mean,
    });
  };

  // Discard pending → snap form state back to the server-known
  // `botConfig`. Mirrors the legacy "Refresh" semantics but local —
  // no network round-trip needed since we already have the row.
  const discardPending = useCallback(() => {
    if (!botConfig) return;
    setBmEnabled(botConfig.enabled);
    setGoodBotAction(botConfig.good_bot_action);
    setBadBotAction(botConfig.bad_bot_action);
    setBmMode(Number(botConfig.bm_mode ?? 1));
    setUnverifiedThreshold(botConfig.unverified_threshold ?? 5);
    setUnverifiedAction(botConfig.unverified_action ?? FirewallAction.CHALLENGE);
    setBmChallengeKind(botConfig.bm_challenge_kind ?? null);
    setCompositeScoringEnabled(botConfig.composite_scoring_enabled ?? false);
    setCompositeBlockThreshold(botConfig.composite_block_threshold ?? 80);
    setNetflowEnabled(botConfig.netflow_enabled ?? false);
    setNetflowKeyMode(botConfig.netflow_key_mode ?? NetflowKeyMode.JA4);
    setPolymorphicJsEnabled(botConfig.polymorphic_js_enabled ?? false);
    setInlineEngineScoring(botConfig.inline_engine_scoring ?? true);
    setAiBotMode(botConfig.bm_ai_bot_enforcement ?? "off");
    setFloors({
      rate_limit: botConfig.bm_rate_limit_max_human_visits ?? 300,
      hammer_floor: botConfig.bm_hammer_floor ?? 50,
      uri_diversity: botConfig.bm_uri_diversity_ratio ?? 0.1,
      url_enum: botConfig.bm_url_enum_floor ?? 20,
      spike: botConfig.bm_spike ?? 30,
      dense: botConfig.bm_dense ?? 60,
      burst_min: botConfig.bm_burst_min_samples ?? 10,
      burst_cv: botConfig.bm_burst_cv_ceiling ?? 0.3,
      burst_mean: botConfig.bm_burst_mean_max ?? 5,
    });
    setPending({});
    setDirty(false);
    setSubmitAttempted(false);
    setTouched({});
  }, [botConfig]);

  const pendingModifiedFields = useMemo(
    () => collectPendingDiff(pending, botConfig, BM_PENDING_LABELS, BM_PENDING_FORMATTERS),
    [pending, botConfig],
  );

  // ── Render ───────────────────────────────────────────────────────
  return (
    <>
      <Helmet title="Bot Management Configuration" />
      <Stack spacing={3} sx={{ p: 6 }}>

        <V2PageHeader
          title="Bot Management Configuration"
          helpTooltip="Configure bot detection for the selected domain: engine toggle, actions for good and bad bots, detection mode, scoring thresholds, signal toggles, and AI bot detection."
          titleSx={{ color: FG1 }}
          sx={{ pb: 0, alignItems: "center" }}
          actions={
            siteID && canReadBotForSite ? (
              <RefreshButton
                onClick={refresh}
                disabled={isRefreshing}
                loading={isRefreshing}
              />
            ) : null
          }
        />

        <SiteScopeGate mode="single">
          {() => (
            <>
        {!canReadBotForSite && selectedSite && (
          <Typography sx={ { fontSize: 14, color: FG2 }}>
            You do not have permission to view bot management for this site in your project role.
          </Typography>
        )}

        {canReadBotForSite && (
          <Box sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 380px" },
            gap: 3,
          }}>
            {/* Main column */}
            <Stack spacing={2}>

              {/* Engine status */}
              <SectionCard
                icon={<PowerSettingsNewOutlinedIcon />}
                title="Bot management engine"
                desc="Turn the bot detection engine on or off. When off, no inspection or scoring is applied to this domain."
                right={
                  <GreenSwitch checked={bmEnabled} disabled={!canConfigureBotForSite}
                    onChange={() => { const v = !bmEnabled; setBmEnabled(v); stagePatch({ enabled: v }); }}
                  />
                }
              />

              {/* Actions */}
              <SectionCard
                icon={<ShieldOutlinedIcon />}
                title="Actions"
                desc="What should happen when a request is classified as a bot. Verified good bots (search engines, monitors) follow a separate path."
              >
                <Box sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 2,
                }}>
                  <Box sx={{
                    p: 2,
                    borderRadius: "12px",
                    border: `1px solid ${showFieldErr("bad_bot_action") ? DANGER : "#F5D0D4"}`,
                    background: "linear-gradient(180deg, #FEF5F5 0%, #FFFBFB 100%)",
                  }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
                      <Box sx={{
                        width: 28, height: 28, borderRadius: "8px",
                        background: "#FEECEE", color: "#A11228",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        "& svg": { width: 16, height: 16 },
                      }}>
                        <GppMaybeOutlinedIcon />
                      </Box>
                      <Typography sx={ { fontSize: 13, fontWeight: 600, color: "#8B1A2A" }}>
                        Bad bots
                        {effectiveEnabled && (
                          <Typography component="span" sx={{ color: DANGER, ml: 0.25 }}>*</Typography>
                        )}
                      </Typography>
                      <Chip label="primary" size="small" sx={ { fontSize: 10.5, height: 18, background: INDIGO_BG, color: INDIGO }} />
                    </Stack>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                      <ActionCard id={FirewallAction.BLOCK} active={badBotAction} disabled={!canConfigureBotForSite}
                        onPick={(v) => { touchField("bad_bot_action"); setBadBotAction(v); stagePatch({ bad_bot_action: v }); }}
                        icon={<BlockOutlinedIcon />} iconBg="#FEECEE" iconColor="#A11228"
                        title="Block" desc="Reject with HTTP 403. Strongest signal but visible to attackers." />
                      <ActionCard id={FirewallAction.CHALLENGE} active={badBotAction} disabled={!canConfigureBotForSite}
                        onPick={(v) => { touchField("bad_bot_action"); setBadBotAction(v); stagePatch({ bad_bot_action: v }); }}
                        icon={<ExtensionOutlinedIcon />} iconBg="#FFF6D0" iconColor="#7a5b10"
                        title="Challenge" desc="Issue a JS / CAPTCHA challenge. Drops headless bots silently." />
                      <ActionCard id="ratelimit" active={badBotAction} disabled={true}
                        onPick={() => {}}
                        icon={<SpeedOutlinedIcon />} iconBg="#EDF3FE" iconColor={INDIGO}
                        title="Rate-limit" desc="Cap throughput per IP. Backend support coming." />
                      <ActionCard id={FirewallAction.LOG} active={badBotAction} disabled={!canConfigureBotForSite}
                        onPick={(v) => { touchField("bad_bot_action"); setBadBotAction(v); stagePatch({ bad_bot_action: v }); }}
                        icon={<VisibilityOutlinedIcon />} iconBg="#F4F4F4" iconColor="#545454"
                        title="Log only" desc="Observe-mode. No user-visible action — useful for tuning." />
                    </Box>
                    {showFieldErr("bad_bot_action") && (
                      <FormHelperText error sx={{ ...helperTextSx, mt: 1 }}>
                        {formValidation.errors.bad_bot_action}
                      </FormHelperText>
                    )}
                  </Box>
                  <Box sx={{
                    p: 2,
                    borderRadius: "12px",
                    border: "1px solid #C8E6D4",
                    background: "linear-gradient(180deg, #F3FBF6 0%, #FAFDFB 100%)",
                  }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
                      <Box sx={{
                        width: 28, height: 28, borderRadius: "8px",
                        background: "#E8FBEF", color: "#0f7a42",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        "& svg": { width: 16, height: 16 },
                      }}>
                        <GppGoodOutlinedIcon />
                      </Box>
                      <Typography sx={ { fontSize: 13, fontWeight: 600, color: "#0f5c38" }}>Good bots</Typography>
                      <Chip label="verified" size="small" sx={ { fontSize: 10.5, height: 18, background: "#E8FBEF", color: "#0f7a42", border: "1px solid #C8E6D4" }} />
                    </Stack>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                      <ActionCard id={FirewallAction.ALLOW} active={goodBotAction} disabled={!canConfigureBotForSite}
                        onPick={(v) => { setGoodBotAction(v); stagePatch({ good_bot_action: v }); }}
                        icon={<CheckCircleOutlineOutlinedIcon />} iconBg="#E8FBEF" iconColor="#0f7a42"
                        title="Allow" desc="Recommended. Trusted crawlers (Google, Bing, Slack…) pass through." />
                      <ActionCard id={FirewallAction.CHALLENGE} active={goodBotAction} disabled={!canConfigureBotForSite}
                        onPick={(v) => { setGoodBotAction(v); stagePatch({ good_bot_action: v }); }}
                        icon={<ExtensionOutlinedIcon />} iconBg="#FFF6D0" iconColor="#7a5b10"
                        title="Challenge" desc="Add a soft challenge for unverified good-bot UAs." />
                    </Box>
                  </Box>
                </Box>
                {/* Challenge kind — required when the engine is enabled.
                    PATCH key is `bm_challenge_kind`. */}
                {effectiveEnabled && (
                  <Box sx={{ mt: 2 }}>
                    <Field
                      label="Challenge kind"
                      required
                      error={showFieldErr("bm_challenge_kind")}
                      hint={!showFieldErr("bm_challenge_kind")
                        ? "Applied whenever a bot action resolves to Challenge."
                        : undefined}
                    >
                      <Select size="small" disabled={!canConfigureBotForSite}
                        value={bmChallengeKind ?? ""}
                        IconComponent={ArrowdownIcon}
                        displayEmpty
                        error={Boolean(showFieldErr("bm_challenge_kind"))}
                        onClose={() => touchField("bm_challenge_kind")}
                        onChange={(e) => {
                          const v = e.target.value;
                          const next = v === "" ? null : v;
                          touchField("bm_challenge_kind");
                          setBmChallengeKind(next);
                          stagePatch({ bm_challenge_kind: next });
                        }}
                        sx={ { fontSize: 14, borderRadius: "8px", height: 38, width: "100%" }}>
                        <MenuItem value="" disabled>
                          <em>Select challenge kind</em>
                        </MenuItem>
                        <MenuItem value={ChallengeKind.INVISIBLE}>Invisible</MenuItem>
                        <MenuItem value={ChallengeKind.MANAGED}>Managed</MenuItem>
                        <MenuItem value={ChallengeKind.ADAPTIVE}>Adaptive</MenuItem>
                        <MenuItem value={ChallengeKind.INTERACTIVE}>Interactive</MenuItem>
                      </Select>
                    </Field>
                  </Box>
                )}
              </SectionCard>

              {/* Detection mode & scoring */}
              <SectionCard
                icon={<TuneOutlinedIcon />}
                title="Detection mode & scoring"
                desc="How aggressively the engine acts on detections, and how unverified or composite scores translate to actions."
                helpTooltip="Choose Off, Log only, or Enforce while the engine is on. Log only records bot scores without blocking; Enforce applies your action thresholds. Unverified and composite scores control when challenges or blocks fire."
              >
                {/* Off / Log only / Enforce — visual + label parity
                    with `.seg` in `schema-validation/index.html`. Maps
                    onto the backend `bm_mode` enum:
                      0 = Off    (no scoring, no enforcement)
                      2 = Shadow (Log only — record everything, block
                          nothing)
                      1 = Enforce (act on the decisions)
                    The Engine-status toggle above is the master kill
                    switch; this segmented chooses how the engine
                    behaves while ON. */}
                <Box sx={{ mb: 2 }}>
                  <Segmented
                    value={bmModeToSegment(bmMode)}
                    disabled={!canConfigureBotForSite}
                    onChange={(v) => {
                      const num = v === "off" ? 0 : v === "log" ? 2 : 1;
                      setBmMode(num);
                      stagePatch({ bm_mode: num });
                    }}
                    options={[
                      { value: "off",     tone: "off",     icon: <PowerSettingsNewOutlinedIcon />, label: "Off" },
                      { value: "log",     tone: "log",     icon: <VisibilityOutlinedIcon />,       label: "Log only" },
                      { value: "enforce", tone: "enforce", icon: <ShieldOutlinedIcon />,           label: "Enforce" },
                    ]}
                  />
                </Box>

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, mb: 2 }}>
                  <Field label="Unverified threshold" hint="Requests scoring at or above this trigger the action below.">
                    <NumStepper value={unverifiedThreshold} disabled={!canConfigureBotForSite}
                      onChange={(v) => { setUnverifiedThreshold(v); stagePatch({ unverified_threshold: v }); }}
                      min={1} max={10} />
                  </Field>
                  <Field label="Unverified action" hint="Applied when score ≥ threshold for unverified clients.">
                    <Select size="small" disabled={!canConfigureBotForSite} value={unverifiedAction}
                      IconComponent={ArrowdownIcon}
                      onChange={(e) => { setUnverifiedAction(e.target.value); stagePatch({ unverified_action: e.target.value }); }}
                      sx={ { fontSize: 14, borderRadius: "8px", height: 38 }}>
                      <MenuItem value={FirewallAction.CHALLENGE}>Challenge</MenuItem>
                      <MenuItem value={FirewallAction.BLOCK}>Block</MenuItem>
                      <MenuItem value={FirewallAction.LOG}>Log only</MenuItem>
                      <MenuItem value={FirewallAction.ALLOW}>Allow</MenuItem>
                      <MenuItem value={FirewallAction.DROP}>Drop</MenuItem>
                    </Select>
                  </Field>
                </Box>

                <Box sx={{ mb: 1.5 }}>
                  <SwitchRow
                    tinted
                    title="Composite scoring"
                    chip={<Chip label="beta" size="small" sx={ { fontSize: 10, height: 16, background: INDIGO_BG, color: INDIGO }} />}
                    desc="Combines fingerprint, behavior & reputation into a single score (0–100). When off, individual signals are used independently."
                    checked={compositeScoringEnabled}
                    disabled={!canConfigureBotForSite}
                    onChange={(e) => { const v = e.target.checked; setCompositeScoringEnabled(v); stagePatch({ composite_scoring_enabled: v }); }}
                  />
                </Box>

                {compositeScoringEnabled && (
                  <Box sx={{ padding: "4px 4px 8px" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 1 }}>
                      <Typography sx={ { fontSize: 12, fontWeight: 500, color: FG2 }}>Composite block threshold</Typography>
                      <Stack direction="row" alignItems="baseline" spacing={1}>
                        <Typography sx={{ fontFamily: "monospace", fontSize: 22, fontWeight: 700, color: INDIGO, letterSpacing: "-0.01em" }}>{compositeBlockThreshold}</Typography>
                        <Typography sx={ { fontSize: 11, color: FG3 }}>/ 100</Typography>
                      </Stack>
                    </Box>
                    <Slider
                      value={compositeBlockThreshold}
                      min={0} max={100} step={1}
                      disabled={!canConfigureBotForSite}
                      onChange={(_, v) => setCompositeBlockThreshold(v)}
                      onChangeCommitted={(_, v) => stagePatch({ composite_block_threshold: v })}
                      sx={{ color: INDIGO }}
                    />
                    <Typography sx={ { fontSize: 11.5, color: FG3, mt: 1 }}>
                      Requests scoring above {compositeBlockThreshold} will be blocked. Lower = more aggressive (more false positives); higher = more permissive (more bots through).
                    </Typography>
                  </Box>
                )}
              </SectionCard>

              {/* Signals */}
              <SectionCard
                icon={<RadioOutlinedIcon />}
                title="Signals"
                desc="Layer-3/4 telemetry and runtime tricks used to enrich the bot score. Disabling reduces fingerprint depth."
              >
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
                  <SwitchRow
                    title="Netflow telemetry"
                    desc="Capture TCP/TLS fingerprints from the edge for deeper classification."
                    checked={netflowEnabled}
                    disabled={!canConfigureBotForSite}
                    onChange={(e) => { const v = e.target.checked; setNetflowEnabled(v); stagePatch({ netflow_enabled: v }); }}
                  />
                  <Field label="Netflow key mode" hint="JA4 is the modern default; JA3 is provided for legacy compatibility.">
                    <Select size="small" disabled={!canConfigureBotForSite || !netflowEnabled} value={netflowKeyMode}
                      IconComponent={ArrowdownIcon}
                      onChange={(e) => { setNetflowKeyMode(e.target.value); stagePatch({ netflow_key_mode: e.target.value }); }}
                      sx={ { fontSize: 14, borderRadius: "8px", height: 38 }}>
                      <MenuItem value={NetflowKeyMode.JA4}>JA4 — TLS fingerprint v2</MenuItem>
                      <MenuItem value={NetflowKeyMode.JA3}>JA3 — legacy</MenuItem>
                      <MenuItem value={NetflowKeyMode.BOTH}>Both — JA3 + JA4</MenuItem>
                    </Select>
                  </Field>
                  <Box sx={{ gridColumn: { md: "1 / -1" } }}>
                    <SwitchRow
                      title="Polymorphic JS challenge"
                      desc="Inject a per-request randomized JS challenge that headless & scripted clients fail."
                      checked={polymorphicJsEnabled}
                      disabled={!canConfigureBotForSite}
                      onChange={(e) => { const v = e.target.checked; setPolymorphicJsEnabled(v); stagePatch({ polymorphic_js_enabled: v }); }}
                    />
                  </Box>
                  <Box sx={{ gridColumn: { md: "1 / -1" } }}>
                    <SwitchRow
                      title="Inline engine scoring"
                      desc="Score each cache-miss request synchronously with the bot management engine. When off, the verdict cache falls back to a counter-based heuristic (challenge everything past the unverified threshold) and the engine is never called on the request path. Recommended on for production sites."
                      checked={inlineEngineScoring}
                      disabled={!canConfigureBotForSite}
                      onChange={(e) => { const v = e.target.checked; setInlineEngineScoring(v); stagePatch({ inline_engine_scoring: v }); }}
                    />
                  </Box>
                </Box>
              </SectionCard>

              {/* AI bot detection — three-state per-site enforcement */}
              <SectionCard
                icon={<SettingsSuggestOutlinedIcon />}
                title="AI bot detection"
                desc="Govern modern LLM crawlers (GPTBot, ClaudeBot, PerplexityBot, ChatGPT-User, CCBot, Bytespider, Google-Extended, …). Pattern list is operator-extensible at runtime via the Redis bm:ai_agents:ua SET. Legacy AI_CRAWLER UAs continue to block independently of this toggle."
                right={<Chip label={aiBotMode === "block" ? "blocking" : aiBotMode === "log_only" ? "logging" : "off"} size="small" sx={ { fontSize: 11, height: 22, background: aiBotMode === "block" ? "#FEE2E2" : aiBotMode === "log_only" ? "#FEF3C7" : colors.neutral[100], color: aiBotMode === "block" ? DANGER : aiBotMode === "log_only" ? WARNING : FG2, borderRadius: "999px" }} />}
              >
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 1.5 }}>
                  {[
                    { id: "off",      title: "Off",      desc: "Modern AI UAs are not specifically detected. Legacy AI_CRAWLER (CCBot/Diffbot/…) continues to block independently. No change from prior behavior." },
                    { id: "log_only", title: "Log only", desc: "Tag matching requests with bot_type=ai_crawler in CH events for visibility; do not block. Use to pilot before turning on enforcement." },
                    { id: "block",    title: "Block",    desc: "Tag and return a 403 for matching requests. Recommended after a log_only pilot period." },
                  ].map((opt) => (
                    <Box
                      key={opt.id}
                      onClick={() => { if (!canConfigureBotForSite || aiBotMode === opt.id) return; setAiBotMode(opt.id); stagePatch({ bm_ai_bot_enforcement: opt.id }); }}
                      sx={{
                        cursor: canConfigureBotForSite ? "pointer" : "not-allowed",
                        padding: "14px 16px",
                        borderRadius: "12px",
                        border: `1px solid ${aiBotMode === opt.id ? INDIGO : BORDER}`,
                        background: aiBotMode === opt.id ? INDIGO_BG : "#fff",
                        opacity: canConfigureBotForSite ? 1 : 0.6,
                        "&:hover": canConfigureBotForSite && aiBotMode !== opt.id ? { background: colors.neutral[50] } : {},
                      }}
                    >
                      <Typography sx={ { fontWeight: 600, fontSize: 14, color: aiBotMode === opt.id ? INDIGO : FG1, mb: 0.5 }}>
                        {opt.title}
                      </Typography>
                      <Typography sx={ { fontSize: 12, color: FG2, lineHeight: 1.45 }}>
                        {opt.desc}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </SectionCard>

              {/* Sensitivity tuning */}
              <SectionCard
                icon={<SettingsSuggestOutlinedIcon />}
                title="Sensitivity tuning"
                desc="Behavior-based detection thresholds. Use a preset for most cases; expand to fine-tune individual floors."
                helpTooltip="Preset behavior-detection floors for rate spikes, URI hammering, and enumeration. Start with Medium; expand the nine advanced floors only when tuning for a specific attack pattern."
                right={<Chip label="engine defaults · recommended" size="small" sx={ { fontSize: 11, height: 22, background: INDIGO_BG, color: INDIGO, borderRadius: "999px" }} />}
              >
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 1.5, mb: 2.5 }}>
                  <SensitivityCard id="low"    active={sensitivity} onPick={onPreset} bars={1} title="Low"
                    desc="Permissive. Best for sites with light bot pressure or many human burst patterns (live events, sales)." />
                  <SensitivityCard id="medium" active={sensitivity} onPick={onPreset} bars={2} title="Medium"
                    desc="Balanced default. Sensible floors for most production traffic. Try this first." />
                  <SensitivityCard id="high"   active={sensitivity} onPick={onPreset} bars={3} title="High"
                    desc="Aggressive. For high-value paths (login, checkout) or while under active scraping campaigns." />
                </Box>

                <Box onClick={() => setAdvanced(!advanced)} sx={{
                  display: "inline-flex", alignItems: "center", gap: 0.5,
                  padding: "6px 12px", borderRadius: "8px", cursor: "pointer",
                  border: `1px solid ${BORDER}`, background: "#fff",
fontSize: 12, fontWeight: 500, color: FG1,
                  "&:hover": { background: colors.neutral[100] },
                  "& svg": { width: 14, height: 14 },
                  mb: advanced ? 2 : 0,
                }}>
                  {advanced ? <ExpandLessOutlinedIcon /> : <ExpandMoreOutlinedIcon />}
                  {advanced ? "Hide" : "Show"} 9 advanced floors
                </Box>

                {advanced && (
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "1fr 1fr 1fr" }, gap: 2 }}>
                    {[
                      ["rate_limit",    "Rate limit",             "Per-IP request ceiling within the window.",         10,    null,     "reqs/120s"],
                      ["hammer_floor",  "Same-URI hammer floor",  "Min same-URI hits before flagging as hammering.",   5],
                      ["uri_diversity", "URI diversity ratio",    null,                                                 0.05, 1],
                      ["url_enum",      "URL enumeration floor",  "Distinct URL hits before enumeration trip.",        5],
                      ["spike",         "Spike (reqs / 1s)",      null,                                                 5],
                      ["dense",         "Dense (reqs / 5s)",      null,                                                 5],
                      ["burst_min",     "Burst min samples",      null,                                                 1],
                      ["burst_cv",      "Burst CV ceiling",       null,                                                 0.05, 2],
                      ["burst_mean",    "Burst mean ≤ (sec)",     null,                                                 1],
                    ].map(([key, label, hint, step, max, suffix]) => (
                      <Field key={key} label={label} hint={hint}>
                        <NumStepper
                          value={floors[key]}
                          disabled={!canConfigureBotForSite}
                          onChange={(v) => {
                            const next = { ...floors, [key]: v };
                            setFloors(next);
                            stagePatch({ [`bm_${key === "rate_limit" ? "rate_limit_max_human_visits"
                              : key === "hammer_floor" ? "hammer_floor"
                              : key === "uri_diversity" ? "uri_diversity_ratio"
                              : key === "url_enum" ? "url_enum_floor"
                              : key === "spike" ? "spike"
                              : key === "dense" ? "dense"
                              : key === "burst_min" ? "burst_min_samples"
                              : key === "burst_cv" ? "burst_cv_ceiling"
                              : "burst_mean_max"}`]: v });
                          }}
                          step={step}
                          max={max ?? Infinity}
                          suffix={suffix}
                        />
                      </Field>
                    ))}
                  </Box>
                )}
              </SectionCard>
            </Stack>

            {/* Right rail */}
            <Box sx={{
              position: { lg: "sticky" }, top: 78, alignSelf: "start",
              maxHeight: { lg: "calc(100vh - 96px)" }, overflowY: { lg: "auto" },
            }}>
              <LiveImpactRail siteUuid={siteID} />
            </Box>
          </Box>
        )}
            </>
          )}
        </SiteScopeGate>
      </Stack>

      {/* Sticky save bar — appears whenever the form has unsaved
          changes. Thin indigo-tinted pill so it reads as a brand
          notification rather than a system overlay, with a slightly
          more saturated border so the edge is visible against the
          neutral page background. */}
      <StickySaveBar
        open={dirty && pendingModifiedFields.length > 0}
        modifiedCount={pendingModifiedFields.length}
        modifiedFields={pendingModifiedFields}
        onDiscard={discardPending}
        onApply={apply}
        isApplying={isApplying}
      />
    </>
  );
};

export default NewServicesBotConfig;
